/*
  Ever Leaf Terms/Privacy consent gate (UPay/Bit/Diners compliance).

  Generic, surface-agnostic: works for the full cart page, the cart drawer, the
  add-to-cart notification popup, and the product page's accelerated/dynamic
  checkout button — anywhere a data-ever-leaf-consent-root wraps one checkbox
  (data-ever-leaf-consent-checkbox) and one or more checkout controls
  (data-ever-leaf-consent-gate).

  Two independent defenses, since a gated control can be either a real <form>
  submit button or an opaque third-party element (Shopify's payment_button
  wallet buttons) we cannot attach a submit listener to:
    1. A capturing-phase 'submit' listener on document, scoped to THIS submit's
       actual submitter (event.submitter) rather than "does this form contain a
       gated control anywhere" — a broader match would incorrectly block a
       form's OTHER submit buttons (e.g. Add to Cart) whenever the checkbox is
       unchecked, even though only the gated control itself requires consent.
    2. Disabling/hiding the gated control(s) directly (disabled attribute for
       buttons, pointer-events:none + aria-disabled for wrapper elements around
       markup we don't control), kept in sync with the checkbox's own change
       event.

  Cart Page hardening: the real <form id="cart"> lives in a different section
  than the #main-cart-footer consent root, so Defense 1's own
  form.closest('[data-ever-leaf-consent-root]') can never resolve it there —
  a second, narrowly-scoped submit listener below re-checks Terms specifically
  for the Cart Page Checkout submitter as defense-in-depth.

  Cart Page Checkout also replaces the native button activation with an
  explicit form.requestSubmit(button) call once Terms is confirmed checked,
  since the native default activation proved unreliable in practice.
*/
(function () {
  'use strict';

  function getRoot(el) {
    return el.closest('[data-ever-leaf-consent-root]');
  }

  function resolveForm(gateEl) {
    if (gateEl.form) return gateEl.form;
    var formAttr = gateEl.getAttribute('form');
    if (formAttr) return document.getElementById(formAttr);
    return gateEl.closest('form');
  }

  function contextLabelForRoot(root) {
    if (!root) return 'unknown';
    var tag = root.tagName ? root.tagName.toLowerCase() : '';
    if (tag === 'cart-drawer') return 'cart-drawer';
    if (tag === 'cart-notification') return 'cart-notification';
    if (root.id === 'main-cart-footer') return 'cart-page';
    if (tag === 'product-form') return 'product-page';
    return tag || root.id || 'unknown';
  }

  // Defense 1: block submission of a form whose ACTUAL submitter (not just
  // any control anywhere inside it) is a gated control, unless Terms is
  // checked. Scoping to event.submitter keeps a form's other submit buttons
  // (e.g. Add to Cart sharing a form with a gated dynamic-checkout button)
  // unaffected. A submit with no submitter (e.g. a script's own
  // form.requestSubmit() with no argument) is never treated as gated.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      var root = getRoot(form);
      if (!root) return;

      var submitter = event.submitter || null;
      var gates = root.querySelectorAll('[data-ever-leaf-consent-gate]');
      var formIsGated = Array.prototype.some.call(gates, function (gateEl) {
        return resolveForm(gateEl) === form && !!submitter && (gateEl === submitter || gateEl.contains(submitter));
      });
      if (!formIsGated) return;

      var checkbox = root.querySelector('[data-ever-leaf-consent-checkbox]');
      if (!checkbox || checkbox.checked) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      var error = root.querySelector('[data-ever-leaf-consent-error]');
      if (error) error.hidden = false;
      checkbox.focus();
      if (checkbox.scrollIntoView) {
        checkbox.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },
    true
  );

  // Cart Page hardening: Defense 1 above is structurally inactive here (see
  // header comment), so this narrowly-scoped defense-in-depth checks Terms
  // specifically for the Cart Page Checkout submitter. Never a blanket
  // "all submit forms require Terms" rule — Product ATC's submitter is never
  // the Cart Page Checkout button, so it is never touched by this listener.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      var submitter = event.submitter;
      if (!submitter) return;

      var cartPageRoot = document.getElementById('main-cart-footer');
      if (!cartPageRoot) return;
      var cartPageGate = cartPageRoot.querySelector('button[data-ever-leaf-consent-gate][type="submit"]');
      if (!cartPageGate || submitter !== cartPageGate) return;

      var checkbox = cartPageRoot.querySelector('[data-ever-leaf-consent-checkbox]');
      var termsOk = checkbox ? checkbox.checked : true;

      if (!termsOk) {
        event.preventDefault();
        event.stopImmediatePropagation();
        var error = cartPageRoot.querySelector('[data-ever-leaf-consent-error]');
        if (error) error.hidden = false;
        if (checkbox) checkbox.focus();
      }
    },
    true
  );

  // Defense 2: keep gated controls disabled/blocked until the checkbox is checked.
  function setGateBlocked(el, blocked) {
    if (el.tagName === 'BUTTON' || el.tagName === 'INPUT') {
      if (blocked) {
        el.disabled = true;
      } else if (el.getAttribute('data-cart-empty') !== 'true') {
        el.disabled = false;
      }
    } else {
      el.classList.toggle('ever-leaf-consent-blocked', blocked);
      el.setAttribute('aria-disabled', blocked ? 'true' : 'false');
    }
  }

  // Cart Page Checkout production requestSubmit fix — a code-level constant,
  // not a runtime-computed flag; changing it back requires an explicit code
  // change, never automatic. Guarded against re-entrant double-submits via
  // cartPageRequestSubmitInFlight below.
  var CART_PAGE_PRODUCTION_REQUESTSUBMIT_ENABLED = true;
  var cartPageRequestSubmitInFlight = false;

  function attemptCartPageRequestSubmitContinuation(form, button, checkbox) {
    if (!CART_PAGE_PRODUCTION_REQUESTSUBMIT_ENABLED) return;
    if (cartPageRequestSubmitInFlight) return;
    // Re-verify Terms live, right here, right before acting — defense in
    // depth for this code path specifically, independent of whether
    // Defense 2's disabling already blocked the click.
    if (!checkbox.checked) return;

    cartPageRequestSubmitInFlight = true;
    try {
      form.requestSubmit(button);
    } catch (e) {
      /* best-effort — native click activation already occurred as a fallback */
    }
    cartPageRequestSubmitInFlight = false;
  }

  function initRoot(root) {
    if (root.dataset.everLeafConsentInit === 'true') return;

    var checkbox = root.querySelector('[data-ever-leaf-consent-checkbox]');
    var gates = root.querySelectorAll('[data-ever-leaf-consent-gate]');
    var error = root.querySelector('[data-ever-leaf-consent-error]');
    if (!checkbox || !gates.length) return;

    root.dataset.everLeafConsentInit = 'true';

    var context = contextLabelForRoot(root);

    function sync() {
      Array.prototype.forEach.call(gates, function (el) {
        setGateBlocked(el, !checkbox.checked);
      });
      if (checkbox.checked && error) error.hidden = true;
    }

    checkbox.addEventListener('change', sync);
    sync();

    // Cart Page Checkout only: replace native button activation with an
    // explicit, synchronous requestSubmit() once Terms is checked. Every
    // other context (cart-drawer, cart-notification, product-page) relies
    // on Defense 1 + Defense 2 alone and is untouched by this branch.
    Array.prototype.forEach.call(gates, function (gateEl) {
      if (context === 'cart-page' && gateEl.tagName === 'BUTTON' && gateEl.type === 'submit') {
        gateEl.addEventListener(
          'click',
          function (event) {
            if (!checkbox.checked) return;

            var freshForm = gateEl.form;
            if (!freshForm) return;

            event.preventDefault();
            attemptCartPageRequestSubmitContinuation(freshForm, gateEl, checkbox);
          },
          true
        );
      }
    });
  }

  function initAll(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-ever-leaf-consent-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme Editor re-renders a section's markup in place on every settings
  // change, which would otherwise leave the new checkbox/gate pair uninitialized.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
